import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearMsalAccounts } from '@/auth';
import { useAuthStore } from '@/stores/useAuthStore';
import { paths } from '@/routes/paths';

/**
 * LocalStorage key for broadcasting logout events across tabs
 */
export const LOGOUT_EVENT_KEY = 'logout-event';

/**
 * Threshold in milliseconds for ignoring stale logout signals
 * Prevents false logouts on page refresh after re-login
 */
export const STALE_THRESHOLD_MS = 10000;

/**
 * Broadcasts a logout signal to other browser tabs via localStorage
 * Call this when the user initiates logout in the current tab
 */
export function broadcastLogout(): void {
  localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());
}

/**
 * Handles storage events to detect logout signals from other tabs
 * Exported separately for testability
 *
 * @param event - The StorageEvent from the window
 * @param clearPermissions - Function to clear auth permissions
 * @param clearQueryCache - Function to clear React Query cache
 * @param navigate - Function to navigate to a route
 */
export function handleStorageEvent(
  event: StorageEvent,
  clearPermissions: () => void,
  clearQueryCache: () => void,
  navigate: (path: string) => void
): void {
  // Only respond to logout-event key
  if (event.key !== LOGOUT_EVENT_KEY) {
    return;
  }

  // Ignore null values (signal cleared)
  if (event.newValue === null) {
    return;
  }

  // Ignore stale signals (older than threshold)
  const signalTimestamp = Number(event.newValue);
  const signalAge = Date.now() - signalTimestamp;
  if (signalAge >= STALE_THRESHOLD_MS) {
    return;
  }

  // Valid logout signal detected - clear all auth state and redirect
  clearMsalAccounts(); // Clear MSAL accounts so LoginPage doesn't redirect back
  clearPermissions();
  clearQueryCache();
  navigate(paths.login);
}

/**
 * Hook to listen for logout events from other browser tabs
 * Automatically clears permissions, query cache, and redirects to login
 * when a logout signal is detected
 */
export function useCrossTabLogout(): void {
  const clearPermissions = useAuthStore((state) => state.clearPermissions);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event: StorageEvent) => {
      handleStorageEvent(
        event,
        clearPermissions,
        () => queryClient.clear(),
        navigate
      );
    };

    window.addEventListener('storage', handler);

    return () => {
      window.removeEventListener('storage', handler);
    };
  }, [clearPermissions, queryClient, navigate]);
}
